import { startTransition, useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut, Menu, Settings } from 'lucide-react';
import { useAuth } from './useAuth';
import { MODULE_REGISTRY } from './nav-items';
import { LocationScopeChip } from './LocationScopeChip';
import { pathAfterClinicSwitch } from './clinic-switch';

// No registry item lives at `/` since the Dashboard moved to `/dashboard`
// (migration 0143 made `/` a redirect to the clinic's landing page), so the
// old exact-match special case for `/` is gone — plain prefix matching now
// covers every item.
function getPageTitle(pathname: string): string {
  const mod = MODULE_REGISTRY.find((m) => pathname.startsWith(m.path));
  return mod?.label ?? 'Ruby HQ';
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function Header({ onMenuToggle }: { onMenuToggle: () => void }) {
  const { user, clinics, selectedClinic, setSelectedClinic, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const pageTitle = getPageTitle(location.pathname);
  const showClinicSelector = clinics.length > 1 || user?.role === 'admin';
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 md:px-6 shrink-0">
      {/* Left: hamburger (mobile) + page title */}
      <div className="flex items-center gap-3">
        <button onClick={onMenuToggle} className="p-1.5 text-gray-500 hover:text-gray-700 md:hidden">
          <Menu size={20} />
        </button>
        <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
      </div>

      {/* Right: clinic selector, bell, user */}
      <div className="flex items-center gap-4">
        {/* Clinic selector */}
        {showClinicSelector && (
          <select
            value={selectedClinic ?? ''}
            onChange={(e) => {
              const id = e.target.value;
              const next = pathAfterClinicSwitch(location.pathname);
              // One transition for both, so the page remounts once, already at
              // its landing path: React Router renders a navigation in a
              // transition, and a clinic set outside it would first remount
              // the old URL (its document id, its filters) under the new
              // clinic. `replace`, so Back can't return to that URL either.
              startTransition(() => {
                setSelectedClinic(id);
                if (next) navigate(next, { replace: true });
              });
            }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 max-w-[40vw] sm:max-w-none"
          >
            {clinics.map((c) => (
              <option key={c.clientId} value={c.clientId}>
                {c.clinicName}
              </option>
            ))}
          </select>
        )}

        {/* Per-user location scope (migration 0161). A scoped user sees shorter
            lists everywhere; without this chip that reads as missing data
            rather than as the intended narrowing. Display only — the server
            enforces. */}
        <LocationScopeChip />

        {/* Notification bell */}
        <button className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-50">
          <Bell size={18} />
        </button>

        {/* User avatar + name + dropdown menu */}
        {user && (
          <div className="relative" ref={menuRef}>
            <button
              data-testid="user-menu-button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-2 rounded-lg px-2 py-1 hover:bg-gray-50 transition-colors"
            >
              <div className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-medium">
                {getInitials(user.name)}
              </div>
              <span className="text-sm font-medium text-gray-700 hidden sm:inline">{user.name}</span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50">
                <div className="px-3 py-2 text-xs text-gray-400 border-b border-gray-100">
                  {user.username}
                </div>
                <button
                  onClick={() => { setMenuOpen(false); navigate('/settings'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Settings size={14} />
                  Account settings
                </button>
                <button
                  onClick={() => { setMenuOpen(false); logout(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        )}
      </div>

    </header>
  );
}
